import React, { useState } from 'react';
import { toast } from 'sonner';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button, MoneyInput, Input } from '@/shared/components/ui';
import type { PopulatedLead } from '../types';
import { Icon } from '@/shared/icons';
/**
 * Props do WinLeadDialog
 */
export interface WinLeadDialogProps {
  /** Lead a ser marcado como ganho */
  lead: PopulatedLead | null;
  /** Controla se o modal está aberto */
  isOpen: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** Callback para confirmar e enviar dados */
  onConfirm: (data: WinLeadData) => void;
  /** Estado de loading durante envio */
  loading?: boolean;
}

/**
 * Dados para marcar lead como ganho
 */
export interface WinLeadData {
  wonValue: number;
  wonDate: string;  // ISO datetime string
  businessStatus: 'won';
}

/**
 * Modal para marcar lead como ganho
 *
 * Chrome = Modal shared (F72). Coleta valor real do negócio e data do ganho.
 * Permite registrar ganhos retroativos.
 *
 * @example
 * ```tsx
 * <WinLeadDialog
 *   lead={selectedLead}
 *   isOpen={isWinDialogOpen}
 *   onClose={() => setIsWinDialogOpen(false)}
 *   onConfirm={handleWinLead}
 *   loading={isUpdating}
 * />
 * ```
 */
export const WinLeadDialog: React.FC<WinLeadDialogProps> = ({
  lead,
  isOpen,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [wonValue, setWonValue] = useState<number | undefined>(lead?.budget);
  const [wonDate, setWonDate] = useState<string>(
    new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:mm
  );

  // Não renderizar se não houver lead
  if (!isOpen || !lead) return null;

  const handleSubmit = () => {
    // Validações
    if (!wonValue || wonValue <= 0) {
      toast.error('Valor do negócio é obrigatório e deve ser maior que zero');
      return;
    }

    if (!wonDate) {
      toast.error('Data do negócio é obrigatória');
      return;
    }

    // Converter data local para ISO datetime
    const wonDateISO = new Date(wonDate).toISOString();

    onConfirm({
      wonValue,
      wonDate: wonDateISO,
      businessStatus: 'won'
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="md">
      <ModalHeader onClose={handleClose}>
        <div
          className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
          style={{ backgroundColor: '#ecfdf5', color: '#059669' }}
        >
          <Icon name="trending-up" className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
          Marcar como Ganho
        </h2>
      </ModalHeader>

      <ModalContent className="space-y-4">
        {/* Lead Info */}
        <div
          className="p-3 rounded-lg"
          style={{
            backgroundColor: '#f1f5f9',
            borderColor: '#e5e7eb',
          }}
        >
          <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
            {lead.contact?.name || 'Sem nome'}
          </p>
          <p className="text-xs" style={{ color: '#64748b' }}>
            {lead.segment}
          </p>
          {lead.budget && (
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              Orçamento estimado: {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(lead.budget)}
            </p>
          )}
        </div>

        {/* Valor do Negócio */}
        <MoneyInput
          label="Valor do Negócio *"
          value={wonValue}
          onChange={setWonValue}
          placeholder="0,00"
          disabled={loading}
        />

        {/* Data do Negócio */}
        <div>
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: '#1e293b' }}
          >
            Data do Negócio *
          </label>
          <Input
            type="datetime-local"
            value={wonDate}
            onChange={(e) => setWonDate(e.target.value)}
            icon={'calendar'}
            disabled={loading}
            data-testid="win-lead-date"
          />
          <p className="text-xs mt-1" style={{ color: '#64748b' }}>
            Data em que o negócio foi fechado (pode ser retroativa)
          </p>
        </div>

        {/* Info box */}
        <div
          className="p-3 rounded-lg text-xs"
          style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #a7f3d0',
            color: '#065f46'
          }}
        >
          <p>
            <strong>Nota:</strong> Ao confirmar, o status do negócio será alterado para "Ganho" e o lead será marcado como convertido.
          </p>
        </div>
      </ModalContent>

      <ModalFooter>
        <Button
          onClick={handleClose}
          variant="secondary"
          size="md"
          disabled={loading}
          data-testid="win-lead-cancel"
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          variant="success"
          size="md"
          disabled={loading}
          data-testid="win-lead-confirm"
        >
          {loading ? 'Salvando...' : 'Confirmar Ganho'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
